// 마크다운 → BlockNote 블록 → Yjs 문서(state) → base64 변환.
//
// 우리 에디터(PageEditor)는 Yjs(getXmlFragment('document-store')) 위에서 BlockNote 협업
// 세션을 구성한다. 따라서 import 본문도 동일한 fragment 이름으로 Yjs 문서를 만든 뒤
// 그 update 를 base64 로 직렬화해 page_content.ystate 에 넣어야 에디터가 그대로 읽는다.
//
// ⚠️ 협업 모드 에디터를 헤드리스(마운트 X)로 만들면 y-prosemirror 바인딩이 동작하지 않아
//    블록이 Yjs fragment 에 기록되지 않는다(빈 ystate 저장 버그). 그래서 BlockNote 가 제공하는
//    blocksToYDoc(editor, blocks, fragment) 로 블록을 직접 Yjs 문서로 직렬화한다.
//
// 실패해도 throw 하지 않고 null 을 반환한다(호출부가 폴백 처리). 성공 시 base64 문자열 반환.

import * as Y from 'yjs';
import { BlockNoteEditor } from '@blocknote/core';
import { blocksToYDoc } from '@blocknote/core/yjs';
import { u8ToBase64, base64ToU8 } from '../editor/collab/yjsBase64';
import { schema, HAS_LINKED_VIEW, HAS_COLUMNS } from '../editor/blocks/schema';

const FRAGMENT_NAME = 'document-store';

// 링크된 DB 뷰 마커(Phase C). notionDirect.js 패스 2(rewriteLinkedViews)가
// 노션 hex → 루트 db id 로 치환해 `root:<uuid>` 형태로 정규화한다.
// root: 매칭 시 props.databaseId = 루트 db UUID.
// 매핑 실패해 notion:<hex> 로 남은 경우는 databaseId 를 비워 둬(빈 문자열)
// DatabaseEmbed 가 폴백 박스를 렌더하게 한다.
const LINKEDVIEW_ROOT = /^:::linkedview\s+root:([0-9a-fA-F-]{8,}):::$/;
// 매핑 실패해 collection:/notion: 로 남은 마커 — 빈 databaseId 폴백 박스로 렌더
const LINKEDVIEW_UNMAPPED = /^:::linkedview\s+(?:notion|collection):[0-9a-fA-F]{32}:::$/;

// ── 임베드류 마커 (2026-07-06) — edge notion-import 의 markerSafeUrl/markerSafeName 과 쌍 ──
// edge 가 노션 embed/video/audio/file/pdf/bookmark/link_preview 블록을 `:::타입 <url>:::` 로
// emit 한다(URL 은 공백 없는 단일 토큰 + ':::' 미포함 보장 — 아니면 edge 가 링크 폴백).
// file 마커만 `:::file <url> | <이름>:::` — URL 먼저 고정 파싱이라 이름 안의 '|' 도 안전.
// 스키마에 해당 블록이 없으면 링크 문단으로 안전 폴백(HAS_LINKED_VIEW 가드와 동일 패턴).
const EMBED_MARKER = /^:::embed\s+(\S+?)\s*:::$/;
const VIDEO_MARKER = /^:::video\s+(\S+?)\s*:::$/;
const AUDIO_MARKER = /^:::audio\s+(\S+?)\s*:::$/;
const FILE_MARKER = /^:::file\s+(\S+?)(?:\s+\|\s+(.+?))?\s*:::$/;
const BOOKMARK_MARKER = /^:::bookmark\s+(\S+?)\s*:::$/;

// ── 마커 페이로드 사전 추출 (2026-07-06 근본 수정: 임베드 URL 밑줄 변형 사고) ──
// 마커 라인은 md 파서를 "일반 텍스트" 로 통과하는데, URL 안의 '_'/'*' 쌍은 emphasis
// 구분자로 소비돼 embed/file url prop 이 실제 스토리지 키·원본 URL 과 달라진다
// (예: notion-zip/.../______x.html → __x.html → iframe 404).
// → 파싱 *전에* 원본 md 라인에서 URL·이름을 그대로 추출해 stash 에 보관하고,
//   마커 라인은 파서-불활성 인덱스 토큰(`:::embed @0:::`)으로 치환한다.
//   파싱 후 매칭 시 @N 토큰을 stash 값으로 복원 → 어떤 URL 이든 바이트 그대로 보존된다.
//   (토큰이 아닌 리터럴 URL 매칭은 기존 경로 그대로 폴백 — 하위호환)
const MARKER_LINE = /^(\s*):::(embed|video|audio|file|bookmark)\s+(\S[^\n]*?)\s*:::\s*$/;

function extractMarkerPayloads(md) {
  const stash = [];
  const lines = String(md).split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const m = lines[i].match(MARKER_LINE);
    if (!m) continue;
    const [, indent, type, payload] = m;
    let url = payload;
    let name = '';
    if (type === 'file') {
      const sep = payload.indexOf(' | ');
      if (sep >= 0) {
        url = payload.slice(0, sep).trim();
        name = payload.slice(sep + 3).trim();
      }
    }
    // URL 이 단일 토큰이 아니면(비정상 페이로드) 원본 라인 유지 — 기존 폴백 경로로 처리
    if (!/^\S+$/.test(url)) continue;
    const idx = stash.push({ url, name }) - 1;
    lines[i] = `${indent}:::${type} @${idx}:::`;
  }
  return { md: lines.join('\n'), stash };
}
const HAS_EMBED = !!schema.blockSchema?.embed;
const HAS_BOOKMARK = !!schema.blockSchema?.bookmark;
const HAS_VIDEO = !!schema.blockSchema?.video;
const HAS_AUDIO = !!schema.blockSchema?.audio;
const HAS_FILE = !!schema.blockSchema?.file;

// 북마크 제목 기본값 — 도메인, 파싱 실패 시 URL 그대로 (Bookmark.jsx 의 기본 제목 규칙과 동일)
function hostOf(url) {
  try { return new URL(url).hostname; } catch (_e) { return ''; }
}

// 스키마 미등록 폴백 — 마커를 만들지 못할 때 기존 동작(일반 링크 문단)으로 되돌린다.
function linkParagraph(url, label) {
  return {
    type: 'paragraph',
    content: [{ type: 'link', href: url, content: [{ type: 'text', text: label || url, styles: {} }] }],
  };
}

// 토글 헤딩 마커(edge notion-import 의 TGL_SENTINEL / :::tgl-start:::/:::tgl-end::: 과 동일).
// edge 가 토글 헤딩 텍스트 끝에 센티넬을 붙이고, 자식을 tgl-start/end 마커로 감싼다.
const TGL_SENTINEL = ' ⟦TGL⟧'; // 헤딩 텍스트 suffix

// 토글 블록 (2026-07-06) — edge 가 노션 toggle 블록을 `- 텍스트 ⟦TGL⟧` (불릿 + 동일 센티넬)로
// emit 한다. BlockNote 0.51.4 기본 스키마(defaultBlockSpecs)에 포함된 toggleListItem 으로 치환.
// 스키마 미등록 환경이면 센티넬만 제거하고 불릿 유지(마커 잔존 방지 — 기존 동작과 동일).
const HAS_TOGGLE = !!schema.blockSchema?.toggleListItem;
const TGL_START = ':::tgl-start:::';
const TGL_END = ':::tgl-end:::';

// ── 멀티 컬럼 마커 (2026-07-06, 스펙 동결 / 2026-07-07 폭 비율 확장) — edge notion-import 가
// 노션 column_list/column 을 아래 4종 마커(전부 독립 문단 라인)로 감싸 emit 한다:
//   :::cols-start <컬럼수>:::        →  columnList 블록 시작
//   :::col-start:::                  →  column 자식 시작 (이후 블록은 그 column 의 children), 폭 비율 없음(균등)
//   :::col-start <비율(0~1)>:::      →  위와 동일 + 노션 원본 width_ratio 보존(예: 0.25, 0.75)
//   :::col-end:::                    →  column 종료
//   :::cols-end:::                   →  columnList 종료
// HAS_COLUMNS=false(스키마 미등록)면 마커 라인만 버리고 자식 블록을 현재 레벨에 순차
// push(기존 평탄화와 동일) — 마커 텍스트를 본문에 남기지 않는다.
// 비율 없는 기존 마커(하위호환: 과거 저장분/구버전 edge)는 기존과 동일하게 균등폭(width 미지정) 처리.
const COLS_START = /^:::cols-start\s+(\d+)\s*:::$/;
const COL_START = /^:::col-start(?:\s+([0-9]*\.?[0-9]+))?\s*:::$/;
const COL_END = ':::col-end:::';
const COLS_END = ':::cols-end:::';

// 콜아웃 센티넬(edge notion-import 의 CO_SENTINEL 과 동일) — edge 가 노션 콜아웃을
// `> {아이콘} {텍스트} ⟦CO⟧` 로 emit 한다. 마크다운 파서는 `>` 를 quote 블록으로 파싱하므로
// 이 센티넬을 보고 quote → callout 커스텀 블록으로 치환한다(선두 아이콘 분리).
// 센티넬 없는 quote(일반 인용문·기존 import 본문)는 그대로 quote 유지.
const CO_SENTINEL = ' ⟦CO⟧';
// 콜아웃 색 센티넬 (2026-07-06) — edge 가 노션 callout.color 가 있을 때만 ` ⟦CO:색⟧` 로 emit
// (색 = gray|brown|red|orange|yellow|green|blue|purple|pink, *_background suffix 는 edge 가 제거).
// 색 없으면 기존 ` ⟦CO⟧` 그대로(하위호환) — 아래 정규식이 두 형태 모두 매칭한다.
const CO_RE = / ⟦CO(?::([a-z]+))?⟧$/;
// 노션 색명 → Callout.jsx props.bg 매핑. rhoot 콜아웃 CSS(.bn-callout-{bg}, page-editor.css +
// dark-theme.css)가 지원하는 색은 gray/blue/yellow/green/red 5종.
// 근사색은 근사 매핑(orange→yellow, pink→red), 나머지 미지원 색(brown/purple)은 gray 폴백.
const CALLOUT_BG = {
  gray: 'gray', blue: 'blue', green: 'green', yellow: 'yellow', red: 'red',
  orange: 'yellow', pink: 'red', brown: 'gray', purple: 'gray',
};
// callout 블록이 스키마에 실제 등록됐는지 — 미등록이면 치환하지 않는다(import 깨짐 방지)
const HAS_CALLOUT = !!schema.blockSchema?.callout;

// 블록 content 의 마지막 text run 에서 센티넬 suffix 를 제거한다(토글 ⟦TGL⟧ / 콜아웃 ⟦CO⟧ 공용).
// inlineText 는 모든 run 의 text 를 결합하므로, 센티넬은 항상 결합 문자열 끝(= 마지막 text run 끝)에 붙는다.
// 마지막 text run 만 안전하게 trim 한다(부분 run 분할 케이스 방지). 제거 성공 여부 반환.
function stripTrailingSentinel(block, sentinel) {
  if (!block || !Array.isArray(block.content) || block.content.length === 0) return false;
  // 결합 텍스트가 센티넬로 끝나는지 먼저 확인
  const joined = inlineText(block);
  if (!joined.endsWith(sentinel)) return false;
  // 마지막으로 text 를 가진 run 을 찾아 suffix 제거
  for (let i = block.content.length - 1; i >= 0; i -= 1) {
    const run = block.content[i];
    if (run && typeof run.text === 'string' && run.text.length) {
      if (run.text.endsWith(sentinel)) {
        run.text = run.text.slice(0, -sentinel.length);
      } else {
        // 센티넬이 마지막 run 내부에 온전히 들어있지 않은 드문 경우 — 결합값 기준 안전 폴백:
        // 마지막 run 의 text 에서 센티넬 길이만큼 끝을 잘라낸다(결합이 센티넬로 끝남이 보장됨).
        run.text = run.text.slice(0, Math.max(0, run.text.length - sentinel.length));
      }
      return true;
    }
  }
  return false;
}

// paragraph 블록의 inline content 텍스트를 결합해 반환
function inlineText(block) {
  if (!block || !Array.isArray(block.content)) return '';
  return block.content
    .map((c) => (c && typeof c.text === 'string' ? c.text : ''))
    .join('');
}

// 임베드류 마커 매칭용 딥 텍스트 — 마커 안의 bare URL 을 GFM autolink 가 link 인라인으로
// 감싸는 경우가 있다(link 는 text 가 content 배열 안에 중첩 → inlineText 로는 URL 이 빠짐).
// link 내부 텍스트까지 재귀 결합한다. 센티넬 strip(inlineText 경로)에는 쓰지 않는다(치환 판정 전용).
function inlineTextDeep(block) {
  const walk = (items) => (Array.isArray(items)
    ? items.map((c) => {
      if (!c) return '';
      if (typeof c.text === 'string') return c.text;
      if (Array.isArray(c.content)) return walk(c.content);
      return '';
    }).join('')
    : '');
  return block && Array.isArray(block.content) ? walk(block.content) : '';
}

// ── 기존 계보 내 교체(in-lineage replace) — 재import "콘텐츠 중복" 근본 수정 (2026-07-05) ──
//
// 배경: 재import 가 새 Y.Doc(새 clientID)의 ystate 로 page_content 를 통째 교체하면
// 기존 계보와 단절된다. 그 페이지를 열어본 브라우저의 IndexedDB 에는 구 계보가 live 로
// 남아 있어, 다음 열람 때 Yjs 병합으로 구+신 콘텐츠가 공존하고 useCollab 의
// normalizeFragmentRoots(내용 보존 병합)가 둘 다 한 blockGroup 에 넣어 저장 → 중복 렌더.
//
// 수정: 기존 ystate 를 base 로 이어받아 그 계보 *안에서* 구 콘텐츠를 삭제(tombstone 이
// 남아 다른 클라이언트 캐시와 병합돼도 삭제가 승리)하고, 새 콘텐츠 블록을 Yjs 수준
// 복제(clone — useCollab.normalizeFragmentRoots 와 동일 패턴)로 base 의 단일 blockGroup
// 에 삽입한다. 실패 시 null 반환 → 호출부가 fresh ystate 로 폴백(현행과 동일).
//
// @param {string} baseYstate - 기존 page_content.ystate (base64)
// @param {Y.Doc} freshDoc - blocksToYDoc 가 만든 새 콘텐츠 문서(fragment=FRAGMENT_NAME)
// @returns {string|null} base64 ystate(기존 계보 유지), 실패 시 null
function replaceIntoBaseLineage(baseYstate, freshDoc) {
  let baseDoc = null;
  try {
    baseDoc = new Y.Doc();
    Y.applyUpdate(baseDoc, base64ToU8(baseYstate));
    const baseFrag = baseDoc.getXmlFragment(FRAGMENT_NAME);
    const freshFrag = freshDoc.getXmlFragment(FRAGMENT_NAME);

    // 새 콘텐츠 top-level 블록 복제 — 변이 전에 완료(복제 실패 시 base 는 건드리지 않음).
    // Y.XmlElement 는 다른 문서에 재부착 불가하므로 clone() 재귀 복제로 이식한다.
    const clones = [];
    for (let g = 0; g < freshFrag.length; g += 1) {
      const grp = freshFrag.get(g);
      if (!grp || grp.nodeName !== 'blockGroup') continue;
      for (let j = 0; j < grp.length; j += 1) clones.push(grp.get(j).clone());
    }

    baseDoc.transact(() => {
      // 기준 그룹: 첫 번째 blockGroup. (오염 상태로 2개+ 있으면 첫 그룹만 남긴다)
      let baseGroup = null;
      for (let i = 0; i < baseFrag.length; i += 1) {
        const el = baseFrag.get(i);
        if (el && el.nodeName === 'blockGroup') { baseGroup = el; break; }
      }
      // 기준 외 루트 전부 삭제 — tombstone 이 남아 캐시 병합 시 삭제가 승리한다.
      for (let i = baseFrag.length - 1; i >= 0; i -= 1) {
        if (baseFrag.get(i) !== baseGroup) baseFrag.delete(i, 1);
      }
      if (!baseGroup) {
        // blockGroup 0개(비정상/빈 계보) — 새로 만들어 계보에 편입
        baseGroup = new Y.XmlElement('blockGroup');
        baseFrag.insert(0, [baseGroup]);
      } else if (baseGroup.length > 0) {
        // 기준 그룹의 기존 콘텐츠 전부 삭제(역시 tombstone 보존)
        baseGroup.delete(0, baseGroup.length);
      }
      if (clones.length > 0) baseGroup.insert(0, clones);
    });

    return u8ToBase64(Y.encodeStateAsUpdate(baseDoc));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[루트/import] 기존 계보 내 교체 실패 — fresh ystate 폴백', e);
    return null;
  } finally {
    try { if (baseDoc) baseDoc.destroy(); } catch (e2) { /* noop */ }
  }
}

/**
 * @param {string} md - 마크다운 본문
 * @param {string|null} [baseYstate] - 기존 page_content.ystate(base64). 주어지면 그 계보
 *   안에서 콘텐츠를 교체(in-lineage replace)해 다른 클라이언트 캐시와 병합돼도 구 콘텐츠가
 *   중복되지 않는다. 없으면(신규 페이지) 현행대로 fresh 문서 생성.
 * @returns {Promise<string|null>} base64 ystate, 실패 시 null
 */
export async function markdownToYstate(md, baseYstate = null) {
  if (!md || !md.trim()) return null;

  let ydoc = null;
  try {
    // 협업/마운트 없이 파싱·직렬화만 하는 헤드리스 에디터.
    // ⚠️ 라이브 에디터(PageEditor)와 동일한 커스텀 schema 로 생성해야
    //    linkedDatabaseView 블록을 blocksToYDoc 가 인식한다(미포함 시 드롭).
    // 임베드류 마커 URL 을 파싱 전에 추출·토큰화(위 extractMarkerPayloads 주석 참조)
    const { md: safeMd, stash: markerStash } = extractMarkerPayloads(md);
    // @N 토큰 → stash 원본 복원. 토큰이 아니면 리터럴 그대로(하위호환 폴백).
    const resolveMarkerPayload = (token, fallbackName) => {
      const pm = /^@(\d+)$/.exec(token || '');
      const p = pm ? markerStash[Number(pm[1])] : null;
      if (p) return p;
      return { url: token, name: (fallbackName || '').trim() };
    };

    const editor = BlockNoteEditor.create({ schema });
    const blocks = await editor.tryParseMarkdownToBlocks(safeMd);
    if (!Array.isArray(blocks) || blocks.length === 0) return null;

    // ── 토글 불릿 치환 (2026-07-06) — `- 텍스트 ⟦TGL⟧` → toggleListItem ──
    // 자식(:::tgl-start:::/:::tgl-end::: 래핑)은 아래 트리 빌더가 lastPushed.children 으로
    // 중첩하므로 여기선 타입 치환 + 센티넬 제거만 한다. 마크다운 들여쓰기로 이미 children 에
    // 중첩된 불릿에도 센티넬이 붙을 수 있어 재귀 순회.
    // HAS_TOGGLE=false 폴백: 센티넬만 제거, 불릿 유지(센티넬 잔존 방지 — 자식 중첩은 동일).
    const convertToggleBullets = (blk) => {
      if (!blk) return;
      if (blk.type === 'bulletListItem' && stripTrailingSentinel(blk, TGL_SENTINEL) && HAS_TOGGLE) {
        blk.type = 'toggleListItem';
      }
      if (Array.isArray(blk.children)) blk.children.forEach(convertToggleBullets);
    };
    blocks.forEach(convertToggleBullets);

    // ── post-parse 변환(Phase C + 토글 헤딩 중첩) ──
    // tryParseMarkdownToBlocks 결과는 flat 배열. 마커를 인식해 (1) 커스텀 블록 치환,
    // (2) :::tgl-start:::/:::tgl-end::: 로 감싼 자식을 직전 헤딩의 children 으로 중첩한다.
    //
    // 스택 기반 트리 빌더:
    //   - root: 최상위 블록 배열. stack[0] = root.
    //   - 일반 블록은 stack top 배열에 push. push 직후 그 블록을 lastPushed 로 기록.
    //   - :::tgl-start::: → lastPushed.children=[] 만들고 그 배열을 stack push(이후 블록은 거기로).
    //   - :::tgl-end::: → stack pop.
    const root = [];
    const stack = [root];
    let lastPushed = null;

    const pushBlock = (block) => {
      stack[stack.length - 1].push(block);
      lastPushed = block;
    };

    // 멀티 컬럼 컨텍스트 스택 — cols-start 로 진입, cols-end 로 종료.
    // block: 조립 중인 columnList, parentArr: columnList 가 push 된 배열(unwrap 폴백용).
    const colListStack = [];

    // columnList 마감 검증 — prosemirror 노드 제약(column='blockContainer+',
    // columnList='column column+')을 못 채우면 에디터 로드가 깨지므로 여기서 보정한다:
    //   (1) 빈 column 제거  (2) 남은 column 이 2개 미만이면 columnList 를 통째로 unwrap
    //       (자식 블록을 columnList 자리에 평탄화 — 콘텐츠는 절대 드롭하지 않는다)
    const finalizeColumnList = (entry) => {
      const { block, parentArr } = entry;
      block.children = (block.children || []).filter(
        (c) => c && c.type === 'column' && Array.isArray(c.children) && c.children.length > 0,
      );
      if (block.children.length < 2) {
        const flat = [];
        block.children.forEach((c) => flat.push(...c.children));
        const idx = parentArr.indexOf(block);
        if (idx >= 0) parentArr.splice(idx, 1, ...flat);
        if (lastPushed === block) lastPushed = flat.length ? flat[flat.length - 1] : null;
      }
    };

    for (let i = 0; i < blocks.length; i += 1) {
      const b = blocks[i];
      if (b && b.type === 'paragraph') {
        const txt = inlineText(b).trim();
        // 토글 자식 영역 시작/끝 마커 — 트리 깊이 조정만 하고 블록은 만들지 않는다.
        if (txt === TGL_START) {
          // 직전에 push 된 블록(보통 토글 헤딩)의 children 으로 진입.
          if (lastPushed) {
            if (!Array.isArray(lastPushed.children)) lastPushed.children = [];
            stack.push(lastPushed.children);
          } else {
            // 방어: tgl-start 앞에 블록이 없으면(비정상) 현재 레벨 유지(스택 불균형 방지 위해 root 재push).
            stack.push(stack[stack.length - 1]);
          }
          continue;
        }
        if (txt === TGL_END) {
          if (stack.length > 1) stack.pop();
          continue;
        }
        // ── 멀티 컬럼 마커 — columnList/column 트리 조립(토글과 동일한 스택 패턴) ──
        const colsStartMatch = txt.match(COLS_START);
        if (colsStartMatch) {
          if (HAS_COLUMNS) {
            const colList = { type: 'columnList', children: [] };
            pushBlock(colList);
            // count: 이 columnList 의 컬럼 수 — 폭 비율(width_ratio) → BlockNote width 정규화에 사용.
            colListStack.push({ block: colList, parentArr: stack[stack.length - 1], count: Number(colsStartMatch[1]) || 0 });
          }
          // HAS_COLUMNS=false: 마커만 버림 — 자식은 col-start 폴백이 현재 레벨에 push.
          continue;
        }
        const colStartMatch = txt.match(COL_START);
        if (colStartMatch) {
          if (HAS_COLUMNS && colListStack.length > 0) {
            const col = { type: 'column', children: [] };
            const ratio = colStartMatch[1] !== undefined ? Number(colStartMatch[1]) : null;
            const top = colListStack[colListStack.length - 1];
            if (ratio != null && Number.isFinite(ratio) && ratio > 0 && top.count > 0) {
              // 노션 column.width_ratio(0~1, 컬럼 폭 비율) → BlockNote column.props.width
              // (flex-grow 값, 기본 1) 매핑. flex-grow 는 같은 columnList 내 상대값이라 스케일은
              // 무관하지만, ratio × 컬럼수로 정규화하면 균등 분배(ratio=1/N) 시 width=1이 되어
              // 비율 정보 없는 기존 컬럼(기본값 1)과 시각적으로 자연스럽게 이어진다.
              col.props = { width: ratio * top.count };
            }
            top.block.children.push(col);
            stack.push(col.children);
          } else {
            // 폴백(스키마 미등록 또는 짝 잃은 마커) — 현재 레벨 재push 로 스택 균형만 유지.
            stack.push(stack[stack.length - 1]);
          }
          continue;
        }
        if (txt === COL_END) {
          if (stack.length > 1) stack.pop();
          continue;
        }
        if (txt === COLS_END) {
          if (colListStack.length > 0) finalizeColumnList(colListStack.pop());
          continue;
        }
        // 빈 줄 보존 — edge 가 노션 빈 문단을 :::blank::: 마커로 보냄 → 빈 문단으로 복원(줄 간격 유지)
        if (txt === ':::blank:::') { pushBlock({ type: 'paragraph' }); continue; }
        if (HAS_LINKED_VIEW) {
          const mr = txt.match(LINKEDVIEW_ROOT);
          if (mr) { pushBlock({ type: 'linkedDatabaseView', props: { databaseId: mr[1] } }); continue; }
          // 매핑 안 된 마커(collection:/notion:) — databaseId 비워 폴백 박스 렌더
          if (LINKEDVIEW_UNMAPPED.test(txt)) { pushBlock({ type: 'linkedDatabaseView', props: { databaseId: '' } }); continue; }
        }
        // 임베드류 마커 (2026-07-06) — edge 가 emit 한 :::embed/video/audio/file/bookmark 마커를
        // 실제 블록으로 치환. 마커 안의 URL 이 autolink 로 link 인라인에 감싸일 수 있어 딥 텍스트로 매칭.
        // 스키마 미등록 블록은 링크 문단 폴백(기존 import 결과와 동일 형태 유지).
        const deep = inlineTextDeep(b).trim();
        if (deep.startsWith(':::')) {
          let m;
          if ((m = deep.match(EMBED_MARKER))) {
            const { url } = resolveMarkerPayload(m[1]);
            if (HAS_EMBED) pushBlock({ type: 'embed', props: { url } });
            else pushBlock(linkParagraph(url));
            continue;
          }
          if ((m = deep.match(VIDEO_MARKER))) {
            const { url } = resolveMarkerPayload(m[1]);
            if (HAS_VIDEO) pushBlock({ type: 'video', props: { url } });
            else pushBlock(linkParagraph(url));
            continue;
          }
          if ((m = deep.match(AUDIO_MARKER))) {
            const { url } = resolveMarkerPayload(m[1]);
            if (HAS_AUDIO) pushBlock({ type: 'audio', props: { url } });
            else pushBlock(linkParagraph(url));
            continue;
          }
          if ((m = deep.match(FILE_MARKER))) {
            const { url, name } = resolveMarkerPayload(m[1], m[2]);
            if (HAS_FILE) pushBlock({ type: 'file', props: { url, name } });
            else pushBlock(linkParagraph(url, name));
            continue;
          }
          if ((m = deep.match(BOOKMARK_MARKER))) {
            const { url } = resolveMarkerPayload(m[1]);
            if (HAS_BOOKMARK) pushBlock({ type: 'bookmark', props: { url, title: hostOf(url) || url } });
            else pushBlock(linkParagraph(url));
            continue;
          }
        }
        // 멀티라인 토글 헤딩 누수 처리 — 노션 "제목<br>설명" 토글 헤딩은 edge 가 헤딩 텍스트 끝에
        // 센티넬을 붙이지만, 헤딩의 soft-break 때문에 마크다운 헤딩이 첫 줄에서 끊겨 "설명⟦TGL⟧" 가
        // 별도 문단으로 떨어지고 센티넬도 그 문단으로 샌다(헤딩은 toggleable 변환 안 됨).
        // → 이 문단을 감지해 (1) 센티넬 제거 (2) 직전 헤딩을 toggleable 로 (3) 설명을 헤딩의
        //    "바로 다음 형제" 로 push. (2026-07-06 수정: 이전엔 children 첫 자식으로 넣었는데,
        //    노션에선 설명이 헤딩 텍스트의 일부(soft break)라 토글이 접혀 있어도 항상 보인다.
        //    children 에 넣으면 기본 접힘 상태에서 설명이 사라짐 → 형제로 두어 항상 보이게.)
        //    진짜 토글 자식(:::tgl-start::: 영역)은 계속 헤딩 children 으로 중첩한다.
        if (inlineText(b).endsWith(TGL_SENTINEL) && lastPushed && lastPushed.type === 'heading') {
          const heading = lastPushed;
          stripTrailingSentinel(b, TGL_SENTINEL);
          heading.props = { ...(heading.props || {}), isToggleable: true };
          if (!Array.isArray(heading.children)) heading.children = [];
          const next = blocks[i + 1];
          const nextIsTglStart = next && next.type === 'paragraph' && inlineText(next).trim() === TGL_START;
          // 설명 문단을 헤딩과 같은 레벨(형제)로 push — 토글 접힘 여부와 무관하게 항상 렌더된다.
          // (children 은 헤딩 블록 안에 중첩되므로 parent 배열 순서는 [heading, 설명] 유지)
          pushBlock(b);
          if (nextIsTglStart) {
            // 뒤따르는 tgl-start 는 형제 설명이 아니라 헤딩 children 진입이어야 하므로
            // (lastPushed 가 방금 push 한 설명 문단으로 바뀌었음) 직접 헤딩 children 으로 진입하고
            // tgl-start 마커는 여기서 소비(건너뜀).
            stack.push(heading.children);
            i += 1;
          }
          continue;
        }
      }
      // 토글 헤딩 — 텍스트가 센티넬로 끝나면 제거 + props.isToggleable=true.
      if (b && b.type === 'heading') {
        const stripped = stripTrailingSentinel(b, TGL_SENTINEL);
        if (stripped) {
          b.props = { ...(b.props || {}), isToggleable: true };
        }
      }
      // 콜아웃 (2026-07-05, 색 지원 2026-07-06) — edge 가 센티넬(⟦CO⟧ 또는 ⟦CO:색⟧)을 붙인
      // quote 를 callout 블록으로 변환. 색 센티넬이면 노션 색명 → bg prop 매핑(CALLOUT_BG).
      // 직후의 :::tgl-start:::/:::tgl-end::: 자식 영역은 위 트리 빌더가 lastPushed(=이 callout)
      // 의 children 으로 중첩한다(토글 헤딩과 동일 경로 — 콜아웃 자식 평탄화 버그 수정).
      const coMatch = HAS_CALLOUT && b && b.type === 'quote'
        ? inlineText(b).match(CO_RE)
        : null;
      if (coMatch) {
        stripTrailingSentinel(b, coMatch[0]);
        // 선두 아이콘 분리 — edge 가 항상 `{아이콘} ` 을 텍스트 앞에 붙인다(기본 💡).
        let icon = '💡';
        const first = b.content.find((c) => c && typeof c.text === 'string' && c.text.length);
        const m = first ? first.text.match(/^(\S{1,12})(?:\s+|$)/u) : null;
        if (m) {
          icon = m[1];
          first.text = first.text.slice(m[0].length);
        }
        pushBlock({
          type: 'callout',
          props: { icon, bg: CALLOUT_BG[coMatch[1]] || 'gray' },
          // 아이콘 분리로 비게 된 text run 은 제거(빈 run 직렬화 방지)
          content: b.content.filter((c) => !(c && c.type === 'text' && c.text === '')),
          children: Array.isArray(b.children) ? b.children : [],
        });
        continue;
      }
      pushBlock(b);
    }

    // 짝 잃은 cols-start(cols-end 누락) 방어 — 남은 columnList 도 동일 검증/보정.
    while (colListStack.length > 0) finalizeColumnList(colListStack.pop());

    // 콜아웃 인라인 병합 후처리 (2026-07-07 이슈 C) — 노션 콜아웃 본문이 페이지 링크 하나뿐이면
    // edge 가 인라인 없는 콜아웃(`> 💡 ⟦CO⟧`) + tgl 자식 문단으로 emit 하고, 트리 빌더가 그
    // 문단을 콜아웃 children 에 붙인다 → 콜아웃 박스 안에 빈 아이콘 줄 + 별도 줄이 생겨
    // 노션(한 줄)과 줄 구성이 달라짐(BUSINESS OS 설명서 사례). tgl 자식은 pushBlock 이후에
    // 붙으므로 변환 시점이 아닌 트리 완성 후에 병합해야 한다: 인라인이 비었고 자식이
    // 손자 없는 단순 문단 1개뿐인 콜아웃은 그 문단을 인라인으로 승격.
    const mergeCalloutSoloChild = (blocks) => {
      for (const blk of blocks) {
        if (!blk) continue;
        if (Array.isArray(blk.children) && blk.children.length) mergeCalloutSoloChild(blk.children);
        if (blk.type !== 'callout') continue;
        const emptyInline = !Array.isArray(blk.content) || blk.content.length === 0;
        if (!emptyInline || !Array.isArray(blk.children) || blk.children.length !== 1) continue;
        const only = blk.children[0];
        const noGrand = !Array.isArray(only?.children) || only.children.length === 0;
        if (only && only.type === 'paragraph' && Array.isArray(only.content) && noGrand) {
          blk.content = only.content;
          blk.children = [];
        }
      }
    };
    mergeCalloutSoloChild(root);

    // 블록 트리 → Yjs 문서(fragment='document-store') 직접 직렬화 (라이브 에디터가 읽는 그 fragment).
    // blocksToYDoc(=blockToNode) 가 각 블록의 nested children 배열을 blockGroup 으로 재귀 직렬화한다.
    ydoc = blocksToYDoc(editor, root, FRAGMENT_NAME);

    // 재import: 기존 ystate 가 있으면 그 계보 안에서 교체(중복 근본 차단).
    // 교체 실패 시 fresh ystate 폴백(경고는 replaceIntoBaseLineage 내부에서 출력).
    if (baseYstate) {
      const inLineage = replaceIntoBaseLineage(baseYstate, ydoc);
      if (inLineage) return inLineage;
    }

    const state = Y.encodeStateAsUpdate(ydoc);
    return u8ToBase64(state);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[루트/import] 마크다운→Yjs 변환 실패, 본문 폴백', e);
    return null;
  } finally {
    try { if (ydoc) ydoc.destroy(); } catch (e2) { /* noop */ }
  }
}
